<template>
  <div class="nps-dashboard-page">
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <div class="loading-text">Loading NPS dashboard…</div>
    </div>

    <header class="dashboard-header">
      <div>
        <div class="logo-text">KNOW<span>ER</span></div>
        <div class="header-subtitle">Dashboard NPS</div>
      </div>
      <div class="header-right">
        <span class="live-dot"></span>
        <span>{{ dashboard.kpis.responses || 0 }} respuestas</span>
      </div>
    </header>

    <filters-bar
      :filters-meta="profile.filters"
      :filters="filters"
      :loading="loading"
      :nps-calification="npsCalification"
      :can-edit-nps-calification="canEditNpsCalification"
      :saving-nps-calification="savingNpsCalification"
      @apply="applyFilters"
      @reset="resetFilters"
      @preset="applyFilters"
      @save-nps-calification="saveNpsCalification"
    />

    <div class="aggregation-bar">
      <button
        v-for="tab in aggregationTabs"
        :key="tab.value"
        class="aggregation-btn"
        :class="{ active: aggregationMode === tab.value }"
        type="button"
        @click="setAggregationMode(tab.value)"
      >
        {{ tab.label }}
      </button>
      <div v-if="storeMappingSummary" class="aggregation-note">
        Tiendas sin match de cluster/segmento: {{ storeMappingSummary.unmatched_devices || 0 }}
      </div>
    </div>

    <main class="dashboard-main">
      <section class="dashboard-section">
        <div class="dashboard-section-title">📊 Indicadores Clave</div>
        <kpi-grid :kpis="dashboard.kpis" />
      </section>

      <section class="dashboard-section">
        <div class="dashboard-section-title">📈 Distribución y Tendencia NPS</div>
        <distribution-section
          :distribution="dashboard.distribution"
          :trend="dashboard.trend_by_day"
        />
      </section>

      <section class="dashboard-section">
        <div class="dashboard-section-title">{{ currentResponsesSectionTitle }}</div>
        <ranking-volume-section
          :top-items="currentTopVolume"
          :bottom-items="currentBottomVolume"
          :top-title="currentVolumeTopTitle"
          :bottom-title="currentVolumeBottomTitle"
        />
      </section>

      <section class="dashboard-section">
        <div class="dashboard-section-title">{{ currentNpsSectionTitle }}</div>
        <ranking-table
          title="Ranking completo con semáforo"
          :entity-label="currentAggregationLabel"
          scroll-hint-label="grupos"
          :rows="currentRankingRows"
          :thresholds="npsThresholds"
        />
      </section>

      <section class="dashboard-section">
        <div class="dashboard-section-title">👤 NPS por Asesor</div>
        <ranking-table
          title="Ranking por asesor"
          entity-label="Asesor"
          scroll-hint-label="asesores"
          :rows="deviceGroupRankingRows"
          :thresholds="npsThresholds"
        />
      </section>

      <section v-if="numeralQuestions.length" class="dashboard-section">
        <div class="dashboard-section-title">⭐ Atributos de Servicio</div>
        <attributes-section v-if="!isGroupedView" :questions="numeralQuestions" :records="dashboard.records" />
        <grouped-attributes-section v-else :questions="numeralQuestions" :records="dashboard.records" :group-key="currentAggregationKey" :group-label="currentAggregationLabel" />
      </section>

      <section class="dashboard-section">
        <div class="dashboard-section-title">💬 Comentarios</div>
        <comments-section :comments="dashboard.comments" />
      </section>

      <section class="dashboard-section">
        <div class="dashboard-section-title">☁️ Nube de Palabras</div>
        <word-cloud-section :word-cloud="dashboard.word_cloud" />
      </section>

      <section v-if="hasCoffeeSection" class="dashboard-section">
        <div class="dashboard-section-title">☕ Café</div>
        <div v-if="!isGroupedView" class="charts-2col-eq">
          <donut-chart-card
            v-if="coffeePurchaseQuestion"
            :title="coffeePurchaseQuestion.label"
            :items="coffeePurchaseItems"
          />
          <div v-if="coffeeStats.length" class="nps-card stats-card">
            <div v-for="item in coffeeStats" :key="item.label" class="stat-item">
              <div class="stat-value" :style="{ color: item.color }">{{ item.value }}</div>
              <div class="stat-label">{{ item.label }}</div>
            </div>
          </div>
        </div>
        <div v-else class="charts-2col-eq">
          <div v-if="coffeePurchaseQuestion" class="group-card-grid">
            <donut-chart-card
              v-for="card in groupedQuestionCards(coffeePurchaseQuestion, 'accent')"
              :key="`coffee-${card.name}`"
              :title="`${coffeePurchaseQuestion.label} · ${card.name}`"
              :items="card.items"
            />
          </div>
          <div v-if="groupedCoffeeMetrics.length" class="nps-card grouped-stats-card">
            <div v-for="item in groupedCoffeeMetrics" :key="item.name" class="group-stat-row">
              <div class="group-stat-name">{{ item.name }}</div>
              <div class="group-stat-values">
                <span v-if="item.satisfaction !== null">Satisfacción: <b>{{ item.satisfaction }}</b></span>
                <span v-if="item.purchaseRate !== null">Compraron café: <b>{{ item.purchaseRate }}</b></span>
              </div>
            </div>
          </div>
        </div>
        <option-breakdown-table
          v-if="coffeePurchaseQuestion"
          :question="coffeePurchaseQuestion"
          :records="dashboard.records"
          :group-key="currentAggregationKey"
          :group-label="currentAggregationLabel"
        />
        <check-chart
          v-if="coffeeImprovementQuestion"
          :campo="coffeeImprovementQuestion.campo"
          :label="coffeeImprovementQuestion.label"
          :rows="dashboard.records"
        />
        <option-breakdown-table
          v-if="coffeeImprovementQuestion"
          :question="coffeeImprovementQuestion"
          :records="dashboard.records"
          :group-key="currentAggregationKey"
          :group-label="currentAggregationLabel"
        />
      </section>

      <section v-if="hasFastFoodSection" class="dashboard-section">
        <div class="dashboard-section-title">🍔 Comida Rápida</div>
        <div v-if="!isGroupedView" class="charts-2col-eq">
          <donut-chart-card
            v-if="fastFoodPurchaseQuestion"
            :title="fastFoodPurchaseQuestion.label"
            :items="fastFoodPurchaseItems"
          />
          <div class="nps-card stats-card fast-food-card">
            <div class="stat-item">
              <div class="stat-value" style="color:#E94560">{{ fastFoodPurchaseRate }}</div>
              <div class="stat-label">Clientes que compran comida rápida</div>
            </div>
          </div>
        </div>
        <div v-else class="charts-2col-eq">
          <div v-if="fastFoodPurchaseQuestion" class="group-card-grid">
            <donut-chart-card
              v-for="card in groupedQuestionCards(fastFoodPurchaseQuestion, 'accent')"
              :key="`fast-food-${card.name}`"
              :title="`${fastFoodPurchaseQuestion.label} · ${card.name}`"
              :items="card.items"
            />
          </div>
          <div v-if="groupedFastFoodMetrics.length" class="nps-card grouped-stats-card">
            <div v-for="item in groupedFastFoodMetrics" :key="item.name" class="group-stat-row">
              <div class="group-stat-name">{{ item.name }}</div>
              <div class="group-stat-values">
                <span>Compran comida rápida: <b>{{ item.purchaseRate }}</b></span>
              </div>
            </div>
          </div>
        </div>
        <option-breakdown-table
          v-if="fastFoodPurchaseQuestion"
          :question="fastFoodPurchaseQuestion"
          :records="dashboard.records"
          :group-key="currentAggregationKey"
          :group-label="currentAggregationLabel"
        />
        <check-chart
          v-if="fastFoodProductQuestion"
          :campo="fastFoodProductQuestion.campo"
          :label="fastFoodProductQuestion.label"
          :rows="dashboard.records"
        />
        <option-breakdown-table
          v-if="fastFoodProductQuestion"
          :question="fastFoodProductQuestion"
          :records="dashboard.records"
          :group-key="currentAggregationKey"
          :group-label="currentAggregationLabel"
        />
      </section>

      <section v-if="priceFairnessQuestion" class="dashboard-section">
        <div class="dashboard-section-title">💰 Percepción de Precios</div>
        <horizontal-bars-card v-if="!isGroupedView" :title="priceFairnessQuestion.label" :items="priceFairnessBars" />
        <div v-else class="group-card-grid">
          <horizontal-bars-card
            v-for="card in groupedBarCards(priceFairnessQuestion)"
            :key="`prices-${card.name}`"
            :title="`${priceFairnessQuestion.label} · ${card.name}`"
            :items="card.items"
          />
        </div>
        <option-breakdown-table
          :question="priceFairnessQuestion"
          :records="dashboard.records"
          :group-key="currentAggregationKey"
          :group-label="currentAggregationLabel"
        />
      </section>

      <section v-if="availabilityQuestion" class="dashboard-section">
        <div class="dashboard-section-title">✅ ¿Encontró lo que buscaba?</div>
        <donut-chart-card v-if="!isGroupedView" :title="availabilityQuestion.label" :items="availabilityItems" />
        <div v-else class="group-card-grid">
          <donut-chart-card
            v-for="card in groupedQuestionCards(availabilityQuestion, 'availability')"
            :key="`availability-${card.name}`"
            :title="`${availabilityQuestion.label} · ${card.name}`"
            :items="card.items"
          />
        </div>
        <option-breakdown-table
          :question="availabilityQuestion"
          :records="dashboard.records"
          :group-key="currentAggregationKey"
          :group-label="currentAggregationLabel"
        />
      </section>

      <section v-if="radioQuestions.length" class="dashboard-section">
        <div class="dashboard-section-title">📻 Preguntas Adicionales</div>
        <div class="charts-2col-eq">
          <radio-chart
            v-for="pregunta in radioQuestions"
            :key="pregunta.campo"
            :campo="pregunta.campo"
            :label="pregunta.label"
            :rows="dashboard.records"
          />
        </div>
      </section>

      <section v-if="checkQuestions.length" class="dashboard-section">
        <div class="dashboard-section-title">☑ Preguntas Adicionales</div>
        <div class="charts-2col-eq">
          <check-chart
            v-for="pregunta in checkQuestions"
            :key="pregunta.campo"
            :campo="pregunta.campo"
            :label="pregunta.label"
            :rows="dashboard.records"
          />
        </div>
      </section>

      <section v-if="dashboard.missing_widgets && dashboard.missing_widgets.length" class="dashboard-section">
        <div class="nps-card info-card">
          Some widgets are hidden because their questions could not be resolved for this round: {{ dashboard.missing_widgets.join(', ') }}.
        </div>
      </section>
    </main>

    <footer class="dashboard-footer">
      <b>KNOWER</b> Consulting · Dashboard NPS · knower.io©️ 2026
    </footer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getDashboard, getProfile, updateNpsCalification } from '@/api/npsReplicaDashboard';
import FiltersBar from '@/components/client/rounds/nps-dashboard/components/FiltersBar.vue';
import KpiGrid from '@/components/client/rounds/nps-dashboard/components/KpiGrid.vue';
import DistributionSection from '@/components/client/rounds/nps-dashboard/components/DistributionSection.vue';
import RankingVolumeSection from '@/components/client/rounds/nps-dashboard/components/RankingVolumeSection.vue';
import RankingTable from '@/components/client/rounds/nps-dashboard/components/RankingTable.vue';
import AttributesSection from '@/components/client/rounds/nps-dashboard/components/AttributesSection.vue';
import GroupedAttributesSection from '@/components/client/rounds/nps-dashboard/components/GroupedAttributesSection.vue';
import CommentsSection from '@/components/client/rounds/nps-dashboard/components/CommentsSection.vue';
import WordCloudSection from '@/components/client/rounds/nps-dashboard/components/WordCloudSection.vue';
import RadioChart from '@/components/client/rounds/nps-dashboard/components/RadioChart.vue';
import CheckChart from '@/components/client/rounds/nps-dashboard/components/CheckChart.vue';
import DonutChartCard from '@/components/client/rounds/nps-dashboard/components/DonutChartCard.vue';
import HorizontalBarsCard from '@/components/client/rounds/nps-dashboard/components/HorizontalBarsCard.vue';
import OptionBreakdownTable from '@/components/client/rounds/nps-dashboard/components/OptionBreakdownTable.vue';

export default {
  name: 'RoundNpsDashboardPage',
  components: {
    FiltersBar,
    KpiGrid,
    DistributionSection,
    RankingVolumeSection,
    RankingTable,
    AttributesSection,
    GroupedAttributesSection,
    CommentsSection,
    WordCloudSection,
    RadioChart,
    CheckChart,
    DonutChartCard,
    HorizontalBarsCard,
    OptionBreakdownTable
  },
  data () {
    return {
      loading: false,
      profile: {
        meta: {
          thresholds: { positive: 50, neutral: 0 },
          nps_calification: '50,0'
        },
        filters: {
          presets: [],
          device_groups: [],
          devices: [],
          device_group_device_map: {},
          date_range: {}
        }
      },
      dashboard: {
        meta: {
          thresholds: { positive: 50, neutral: 0 },
          nps_calification: '50,0'
        },
        kpis: {
          nps: 0,
          status: 'neutral',
          responses: 0,
          promoters: { count: 0, pct: 0 },
          neutrals: { count: 0, pct: 0 },
          detractors: { count: 0, pct: 0 }
        },
        distribution: [],
        trend_by_day: [],
        rankings: {
          devices: { top_volume: [], bottom_volume: [], table: [] },
          device_groups: { table: [] }
        },
        comments: { promoters: [], neutrals: [], detractors: [] },
        word_cloud: { promoters: [], neutrals: [], detractors: [] },
        records: [],
        missing_widgets: []
      },
      filters: {
        start_date: null,
        end_date: null,
        device_groups: [],
        devices: []
      },
      aggregationMode: 'device',
      savingNpsCalification: false
    };
  },
  computed: {
    ...mapGetters(['currentUser']),
    roundId () {
      return this.$route.params.round_id;
    },
    canEditNpsCalification () {
      return this.currentUser && this.currentUser.role !== 'manager';
    },
    aggregationTabs () {
      return [
        { value: 'device', label: 'Tienda' },
        { value: 'cluster', label: 'Cluster' },
        { value: 'segment', label: 'Segmento' }
      ];
    },
    isGroupedView () {
      return this.aggregationMode !== 'device';
    },
    currentAggregationKey () {
      const keys = {
        device: 'device',
        cluster: 'cluster',
        segment: 'segment'
      };

      return keys[this.aggregationMode] || 'device';
    },
    currentAggregationLabel () {
      const labels = {
        device: 'Tienda',
        cluster: 'Cluster',
        segment: 'Segmento'
      };

      return labels[this.aggregationMode] || 'Tienda';
    },
    currentResponsesSectionTitle () {
      return `🏪 Respuestas por ${this.currentAggregationLabel}`;
    },
    currentNpsSectionTitle () {
      return `🎯 NPS por ${this.currentAggregationLabel}`;
    },
    currentVolumeTopTitle () {
      return `🏆 TOP 10 - Mayor volumen por ${this.currentAggregationLabel}`;
    },
    currentVolumeBottomTitle () {
      return `⬇ BOTTOM 10 - Menor volumen por ${this.currentAggregationLabel}`;
    },
    deviceTopVolume () {
      return this.dashboard.rankings && this.dashboard.rankings.devices ? this.dashboard.rankings.devices.top_volume : [];
    },
    deviceBottomVolume () {
      return this.dashboard.rankings && this.dashboard.rankings.devices ? this.dashboard.rankings.devices.bottom_volume : [];
    },
    deviceRankingRows () {
      return this.dashboard.rankings && this.dashboard.rankings.devices ? this.dashboard.rankings.devices.table : [];
    },
    deviceGroupRankingRows () {
      return this.dashboard.rankings && this.dashboard.rankings.device_groups ? this.dashboard.rankings.device_groups.table : [];
    },
    currentRankingRows () {
      if (!this.isGroupedView) {
        return this.deviceRankingRows;
      }

      return this.buildRankingRows(this.currentAggregationKey);
    },
    currentTopVolume () {
      if (!this.isGroupedView) {
        return this.deviceTopVolume;
      }

      return this.sliceVolumeRows(this.currentRankingRows, true);
    },
    currentBottomVolume () {
      if (!this.isGroupedView) {
        return this.deviceBottomVolume;
      }

      return this.sliceVolumeRows(this.currentRankingRows, false);
    },
    featuredQuestions () {
      return this.profile.meta && this.profile.meta.featured_questions ? this.profile.meta.featured_questions : {};
    },
    optionTableQuestions () {
      return this.profile.meta && this.profile.meta.option_table_questions ? this.profile.meta.option_table_questions : {};
    },
    featuredQuestionIds () {
      return Object.keys(this.featuredQuestions)
        .map((key) => this.featuredQuestions[key])
        .filter(Boolean)
        .map((question) => question.id);
    },
    optionTableQuestionIds () {
      return Object.keys(this.optionTableQuestions)
        .map((key) => this.optionTableQuestions[key])
        .filter(Boolean)
        .map((question) => question.id);
    },
    excludedAdditionalQuestionIds () {
      return this.featuredQuestionIds.concat(this.optionTableQuestionIds);
    },
    numeralQuestions () {
      return this.baseQuestions('preguntas_numeral', 'numeral_questions');
    },
    radioQuestions () {
      return this.baseQuestions('preguntas_radio', 'radio_questions');
    },
    checkQuestions () {
      return this.baseQuestions('preguntas_check', 'check_questions');
    },
    coffeePurchaseQuestion () {
      return this.featuredQuestions.coffee_purchase || null;
    },
    coffeeSatisfactionQuestion () {
      return this.featuredQuestions.coffee_satisfaction || null;
    },
    fastFoodPurchaseQuestion () {
      return this.featuredQuestions.fast_food_purchase || null;
    },
    priceFairnessQuestion () {
      return this.featuredQuestions.price_fairness || null;
    },
    availabilityQuestion () {
      return this.featuredQuestions.availability || null;
    },
    fastFoodProductQuestion () {
      return this.optionTableQuestions.fast_food_product || null;
    },
    coffeeImprovementQuestion () {
      return this.optionTableQuestions.coffee_improvement || null;
    },
    hasCoffeeSection () {
      return !!(this.coffeePurchaseQuestion || this.coffeeSatisfactionQuestion);
    },
    hasFastFoodSection () {
      return !!this.fastFoodPurchaseQuestion;
    },
    coffeePurchaseItems () {
      return this.questionDistributionItems(this.coffeePurchaseQuestion, 'accent');
    },
    fastFoodPurchaseItems () {
      return this.questionDistributionItems(this.fastFoodPurchaseQuestion, 'accent');
    },
    availabilityItems () {
      return this.questionDistributionItems(this.availabilityQuestion, 'availability');
    },
    priceFairnessBars () {
      return this.questionDistribution(this.priceFairnessQuestion).map((item) => ({
        label: item.label,
        value: item.pct,
        displayValue: `${item.pct.toFixed(1)}%`,
        color: '#E94560'
      }));
    },
    coffeeStats () {
      const stats = [];

      if (this.coffeeSatisfactionQuestion) {
        stats.push({
          label: this.coffeeSatisfactionQuestion.label || 'Satisfacción café (0-10)',
          value: this.formatNumber(this.averageForQuestion(this.coffeeSatisfactionQuestion)),
          color: '#E94560'
        });
      }

      if (this.coffeePurchaseQuestion) {
        stats.push({
          label: 'Compraron café',
          value: this.formatPct(this.positiveRateForQuestion(this.coffeePurchaseQuestion)),
          color: '#28A745'
        });
      }

      return stats;
    },
    fastFoodPurchaseRate () {
      if (!this.fastFoodPurchaseQuestion) {
        return '0.0%';
      }

      return this.formatPct(this.positiveRateForQuestion(this.fastFoodPurchaseQuestion));
    },
    groupedRecordsByCurrent () {
      return this.groupRecordsBy(this.currentAggregationKey);
    },
    currentGroupNames () {
      return this.sortedGroupNames(Object.keys(this.groupedRecordsByCurrent));
    },
    groupedCoffeeMetrics () {
      if (!this.isGroupedView) {
        return [];
      }

      return this.currentGroupNames.map((groupName) => {
        const records = this.groupedRecordsByCurrent[groupName] || [];

        return {
          name: groupName,
          satisfaction: this.coffeeSatisfactionQuestion ? this.formatNumber(this.averageForQuestion(this.coffeeSatisfactionQuestion, records)) : null,
          purchaseRate: this.coffeePurchaseQuestion ? this.formatPct(this.positiveRateForQuestion(this.coffeePurchaseQuestion, records)) : null
        };
      });
    },
    groupedFastFoodMetrics () {
      if (!this.isGroupedView || !this.fastFoodPurchaseQuestion) {
        return [];
      }

      return this.currentGroupNames.map((groupName) => {
        const records = this.groupedRecordsByCurrent[groupName] || [];

        return {
          name: groupName,
          purchaseRate: this.formatPct(this.positiveRateForQuestion(this.fastFoodPurchaseQuestion, records))
        };
      });
    },
    storeMappingSummary () {
      return this.dashboard.meta && this.dashboard.meta.store_mapping
        ? this.dashboard.meta.store_mapping
        : (this.profile.meta && this.profile.meta.store_mapping ? this.profile.meta.store_mapping : null);
    },
    npsThresholds () {
      return this.dashboard.meta && this.dashboard.meta.thresholds
        ? this.dashboard.meta.thresholds
        : (this.profile.meta && this.profile.meta.thresholds ? this.profile.meta.thresholds : { positive: 50, neutral: 0 });
    },
    npsCalification () {
      return this.profile.meta && this.profile.meta.nps_calification
        ? this.profile.meta.nps_calification
        : (this.dashboard.meta && this.dashboard.meta.nps_calification ? this.dashboard.meta.nps_calification : '50,0');
    }
  },
  created () {
    this.loadProfile();
  },
  methods: {
    baseQuestions (primaryKey, legacyKey) {
      if (!this.profile.meta) {
        return [];
      }

      const questions = this.profile.meta[primaryKey] || this.profile.meta[legacyKey] || [];
      return questions.filter((question) => this.excludedAdditionalQuestionIds.indexOf(question.id) === -1);
    },
    loadProfile () {
      this.loading = true;
      getProfile(this.roundId, this.currentUser ? this.currentUser.id : null)
        .then((response) => {
          this.profile = response.data.data;
          this.filters = this.defaultFilters();
          this.loadDashboard();
        })
        .catch((error) => {
          this.loading = false;
          this.$store.dispatch('setSnackbar', {
            show: true,
            color: 'error',
            text: error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Unable to load dashboard profile.'
          });
        });
    },
    loadDashboard () {
      this.loading = true;
      getDashboard(this.roundId, this.serializeFilters(this.filters), this.currentUser ? this.currentUser.id : null)
        .then((response) => {
          this.dashboard = response.data.data;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.$store.dispatch('setSnackbar', {
            show: true,
            color: 'error',
            text: error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Unable to load dashboard data.'
          });
        });
    },
    setAggregationMode (mode) {
      this.aggregationMode = mode;
    },
    applyFilters (filters) {
      this.filters = {
        start_date: filters.start_date || this.defaultDateRange().from,
        end_date: filters.end_date || this.defaultDateRange().to,
        device_groups: filters.device_groups || [],
        devices: filters.devices || []
      };
      this.loadDashboard();
    },
    resetFilters () {
      this.filters = this.defaultFilters();
      this.loadDashboard();
    },
    saveNpsCalification (value) {
      this.savingNpsCalification = true;
      updateNpsCalification(this.roundId, value)
        .then((response) => {
          const data = response.data && response.data.data ? response.data.data : {};
          this.profile.meta = Object.assign({}, this.profile.meta, {
            nps_calification: data.nps_calification || '50,0',
            thresholds: data.thresholds || { positive: 50, neutral: 0 }
          });
          this.dashboard.meta = Object.assign({}, this.dashboard.meta, {
            nps_calification: data.nps_calification || '50,0',
            thresholds: data.thresholds || { positive: 50, neutral: 0 }
          });
          this.savingNpsCalification = false;
          this.loadDashboard();
        })
        .catch((error) => {
          this.savingNpsCalification = false;
          this.$store.dispatch('setSnackbar', {
            show: true,
            color: 'error',
            text: error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Unable to save NPS calification.'
          });
        });
    },
    defaultFilters () {
      const dateRange = this.defaultDateRange();

      return {
        start_date: dateRange.from,
        end_date: dateRange.to,
        device_groups: [],
        devices: []
      };
    },
    defaultDateRange () {
      if (this.profile.filters && this.profile.filters.date_range) {
        return {
          from: this.profile.filters.date_range.default_from || new Date().toISOString().slice(0, 10),
          to: this.profile.filters.date_range.default_to || new Date().toISOString().slice(0, 10)
        };
      }

      const today = new Date().toISOString().slice(0, 10);
      return { from: today, to: today };
    },
    serializeFilters (filters) {
      return {
        start_date: filters.start_date,
        end_date: filters.end_date,
        device_groups: filters.device_groups,
        devices: filters.devices
      };
    },
    formatPct (value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
    formatNumber (value) {
      return Number(value || 0).toFixed(1);
    },
    averageForQuestion (question, records) {
      if (!question) {
        return 0;
      }

      const scopedRecords = records || this.dashboard.records;
      let sum = 0;
      let count = 0;

      scopedRecords.forEach((record) => {
        const answer = this.answerForRecord(record, question);
        if (answer && answer.numeric !== null) {
          sum += Number(answer.numeric);
          count++;
        }
      });

      return count > 0 ? sum / count : 0;
    },
    positiveRateForQuestion (question, records) {
      const distribution = this.questionDistribution(question, records);
      const total = distribution.reduce((sum, item) => sum + item.value, 0);
      const positive = distribution
        .filter((item) => this.isPositiveChoice(item.label))
        .reduce((sum, item) => sum + item.value, 0);

      return total > 0 ? (positive / total) * 100 : 0;
    },
    questionDistributionItems (question, palette, records) {
      return this.questionDistribution(question, records).map((item, index) => ({
        label: item.label,
        value: item.value,
        pct: item.pct,
        color: this.resolveDistributionColor(item.label, index, palette)
      }));
    },
    questionDistribution (question, records) {
      if (!question) {
        return [];
      }

      const scopedRecords = records || this.dashboard.records;
      const counts = {};
      const multiple = question.type === 'CHECK';

      scopedRecords.forEach((record) => {
        const answer = this.answerForRecord(record, question);
        const values = this.extractAnswerValues(answer, multiple);

        values.forEach((value) => {
          if (!counts[value]) {
            counts[value] = 0;
          }
          counts[value]++;
        });
      });

      const total = Object.keys(counts).reduce((sum, key) => sum + counts[key], 0);

      return Object.keys(counts)
        .map((label) => ({
          label,
          value: counts[label],
          pct: total > 0 ? (counts[label] / total) * 100 : 0
        }))
        .sort((left, right) => {
          if (left.value === right.value) {
            return left.label.localeCompare(right.label);
          }

          return right.value - left.value;
        });
    },
    buildRankingRows (dimensionKey) {
      const grouped = {};

      this.dashboard.records
        .forEach((record) => {
          const dimensionValue = record.dimensions && record.dimensions[dimensionKey] ? record.dimensions[dimensionKey] : 'SIN_MAPEO';

          if (!grouped[dimensionValue]) {
            grouped[dimensionValue] = {
              name: dimensionValue,
              responses: 0,
              npsResponses: 0,
              promoters: 0,
              neutrals: 0,
              detractors: 0
            };
          }

          grouped[dimensionValue].responses++;
          if (record.nps_score !== null) {
            grouped[dimensionValue].npsResponses++;
            grouped[dimensionValue][this.resolveNpsBucket(record.nps_score)]++;
          }
        });

      return Object.keys(grouped)
        .map((key) => {
          const group = grouped[key];
          const nps = this.calculateNps(group.promoters, group.detractors, group.npsResponses);

          return {
            name: group.name,
            responses: group.responses,
            nps_responses: group.npsResponses,
            promoter_pct: this.percentage(group.promoters, group.npsResponses),
            neutral_pct: this.percentage(group.neutrals, group.npsResponses),
            detractor_pct: this.percentage(group.detractors, group.npsResponses),
            nps
          };
        })
        .sort((left, right) => {
          if (left.nps === right.nps) {
            if (left.responses === right.responses) {
              return left.name.localeCompare(right.name);
            }

            return right.responses - left.responses;
          }

          return right.nps - left.nps;
        });
    },
    sliceVolumeRows (rows, descending) {
      return rows.slice()
        .sort((left, right) => {
          if (left.responses === right.responses) {
            return left.name.localeCompare(right.name);
          }

          return descending ? right.responses - left.responses : left.responses - right.responses;
        })
        .slice(0, 10)
        .map((row) => ({
          name: row.name,
          responses: row.responses
        }));
    },
    groupRecordsBy (dimensionKey) {
      const grouped = {};

      this.dashboard.records.forEach((record) => {
        const dimensionValue = record.dimensions && record.dimensions[dimensionKey] ? record.dimensions[dimensionKey] : 'SIN_MAPEO';
        if (!grouped[dimensionValue]) {
          grouped[dimensionValue] = [];
        }

        grouped[dimensionValue].push(record);
      });

      return grouped;
    },
    sortedGroupNames (names) {
      return names.slice().sort((left, right) => {
        if (left === 'SIN_MAPEO') {
          return 1;
        }

        if (right === 'SIN_MAPEO') {
          return -1;
        }

        return left.localeCompare(right);
      });
    },
    groupedQuestionCards (question, palette) {
      if (!question || !this.isGroupedView) {
        return [];
      }

      return this.currentGroupNames.map((groupName) => ({
        name: groupName,
        items: this.questionDistributionItems(question, palette, this.groupedRecordsByCurrent[groupName] || [])
      }));
    },
    groupedBarCards (question) {
      if (!question || !this.isGroupedView) {
        return [];
      }

      return this.currentGroupNames.map((groupName) => ({
        name: groupName,
        items: this.questionDistribution(question, this.groupedRecordsByCurrent[groupName] || []).map((item) => ({
          label: item.label,
          value: item.pct,
          displayValue: `${item.pct.toFixed(1)}%`,
          color: '#E94560'
        }))
      }));
    },
    resolveNpsBucket (score) {
      if (score >= 9) {
        return 'promoters';
      }

      if (score >= 7) {
        return 'neutrals';
      }

      return 'detractors';
    },
    percentage (value, total) {
      if (!total) {
        return 0;
      }

      return Number((((value || 0) / total) * 100).toFixed(2));
    },
    calculateNps (promoters, detractors, total) {
      if (!total) {
        return 0;
      }

      return Math.round(((promoters - detractors) / total) * 100);
    },
    answerForRecord (record, question) {
      if (!record || !record.answers || !question) {
        return null;
      }

      return record.answers[question.campo] || record.answers[question.id] || null;
    },
    extractAnswerValues (answer, multiple) {
      if (!answer) {
        return [];
      }

      if (multiple) {
        const parsed = this.parseStructuredValue(answer.raw_value);
        if (parsed.length > 0) {
          return parsed;
        }

        const fallback = this.parseStructuredValue(answer.label);
        if (fallback.length > 0) {
          return fallback;
        }

        return [];
      }

      const normalized = this.normalizeValue(answer.label);
      return normalized ? [normalized] : [];
    },
    parseStructuredValue (value) {
      if (value === null || value === undefined || value === '') {
        return [];
      }

      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeValue(item)).filter(Boolean);
      }

      if (typeof value === 'object') {
        return Object.keys(value)
          .filter((key) => this.isTruthySelection(value[key]))
          .map((key) => this.normalizeValue(key))
          .filter(Boolean);
      }

      const normalized = String(value).trim();
      if (!normalized) {
        return [];
      }

      if ((normalized[0] === '[' && normalized[normalized.length - 1] === ']') || (normalized[0] === '{' && normalized[normalized.length - 1] === '}')) {
        try {
          return this.parseStructuredValue(JSON.parse(normalized));
        } catch (error) {
        }
      }

      if (/[,;|]/.test(normalized)) {
        return normalized.split(/[,;|]/).map((item) => this.normalizeValue(item)).filter(Boolean);
      }

      return [normalized];
    },
    isTruthySelection (value) {
      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'number') {
        return value === 1;
      }

      return /^(true|1|si|sí|yes|on)$/i.test(String(value).trim());
    },
    normalizeValue (value) {
      if (value === null || value === undefined) {
        return null;
      }

      const normalized = String(value).trim();
      return normalized || null;
    },
    isPositiveChoice (label) {
      return /^(si|sí|yes|true|1|encontro|encontr[oó]|fair|justo|justos)$/i.test(String(label).trim());
    },
    isNegativeChoice (label) {
      return /^(no|false|0)$/i.test(String(label).trim());
    },
    resolveDistributionColor (label, index, palette) {
      if (palette === 'availability') {
        if (this.isPositiveChoice(label)) {
          return '#28A745';
        }

        if (this.isNegativeChoice(label)) {
          return '#DC3545';
        }
      }

      if (palette === 'accent') {
        if (this.isPositiveChoice(label)) {
          return '#E94560';
        }

        if (this.isNegativeChoice(label)) {
          return '#6B7280';
        }
      }

      const colors = ['#E94560', '#6B7280', '#6366F1', '#FFC107', '#28A745', '#DC3545'];
      return colors[index % colors.length];
    }
  }
};
</script>

<style scoped>
.nps-dashboard-page {
  --bg-dark: #1A1A2E;
  --accent: #E94560;
  --bg-card: #FFFFFF;
  --bg-page: #F0F2F8;
  --text-primary: #1A1A2E;
  --text-muted: #6B7280;
  --positive: #28A745;
  --negative: #DC3545;
  --neutral-color: #FFC107;
  --purple: #6366F1;
  --border: #E2E8F0;
  --card-shadow: 0 2px 12px rgba(26,26,46,0.08);
  background: var(--bg-page);
  color: var(--text-primary);
  font-family: 'Segoe UI', Calibri, Inter, sans-serif;
  font-size: 14px;
  margin: 0 -24px -24px;
  min-height: 100vh;
}

.dashboard-header {
  align-items: center;
  background: var(--bg-dark);
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  color: #fff;
  display: flex;
  height: 60px;
  justify-content: space-between;
  padding: 0 28px;
  position: relative;
  z-index: 2;
}

.logo-text {
  color: #fff;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 3px;
}

.logo-text span,
.dashboard-footer b {
  color: var(--accent);
}

.header-subtitle {
  color: rgba(255,255,255,0.55);
  font-size: 11px;
  letter-spacing: 1px;
  margin-top: 2px;
  text-transform: uppercase;
}

.header-right {
  align-items: center;
  color: rgba(255,255,255,0.5);
  display: flex;
  font-size: 12px;
  gap: 8px;
}

.live-dot {
  animation: blink 2s infinite;
  background: var(--positive);
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  width: 8px;
}

.dashboard-main {
  margin: 0 auto;
  max-width: 1600px;
  padding: 24px 28px;
}

.aggregation-bar {
  align-items: center;
  background: #FFFFFF;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 28px;
}

.aggregation-btn {
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 14px;
}

.aggregation-btn.active {
  background: var(--bg-dark);
  border-color: var(--bg-dark);
  color: #fff;
}

.aggregation-note {
  color: var(--text-muted);
  font-size: 12px;
  margin-left: auto;
}

.dashboard-section {
  margin-bottom: 28px;
}

.dashboard-section-title {
  align-items: center;
  color: var(--text-muted);
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  letter-spacing: 1px;
  margin: 28px 0 14px;
  text-transform: uppercase;
}

.dashboard-section-title::after {
  background: var(--border);
  content: '';
  flex: 1;
  height: 1px;
}

.charts-2col-eq {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
}

.nps-card {
  background: var(--bg-card);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  padding: 20px 22px;
}

.group-card-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.grouped-stats-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
}

.group-stat-row {
  border-bottom: 1px solid var(--border);
  padding-bottom: 10px;
}

.group-stat-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.group-stat-name {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 4px;
}

.group-stat-values {
  color: var(--text-muted);
  display: flex;
  flex-direction: column;
  font-size: 12px;
  gap: 4px;
}

.stats-card {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
}

.fast-food-card {
  min-height: 320px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 28px;
  font-weight: 800;
}

.stat-label {
  color: var(--text-muted);
  font-size: 11px;
  margin-top: 4px;
  text-transform: uppercase;
}

.info-card {
  color: var(--text-muted);
  font-size: 12px;
}

.dashboard-footer {
  background: var(--bg-dark);
  color: rgba(255,255,255,0.4);
  font-size: 11px;
  letter-spacing: 0.5px;
  margin-top: 40px;
  padding: 16px;
  text-align: center;
}

.loading-overlay {
  align-items: center;
  background: var(--bg-dark);
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  inset: 0;
  justify-content: center;
  position: fixed;
  z-index: 9999;
}

.spinner {
  animation: spin 0.8s linear infinite;
  border: 3px solid rgba(255,255,255,0.1);
  border-radius: 50%;
  border-top-color: var(--accent);
  height: 40px;
  width: 40px;
}

.loading-text {
  color: rgba(255,255,255,0.6);
  font-size: 13px;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .dashboard-main {
    padding: 16px;
  }

  .aggregation-bar {
    padding: 12px 16px;
  }

  .aggregation-note {
    margin-left: 0;
    width: 100%;
  }

  .charts-2col-eq {
    grid-template-columns: 1fr;
  }

  .dashboard-header {
    padding: 0 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/client/rounds/nps-dashboard/Page.vue